import BaseService from './base.service'
import baseApi from './base.api'

import type { AssessmentsStudentType, AssessmentsEvaluatorType, AssessmentsEvaluatorItemsType, AssessmentCorrectionType } from '@/types/types'

export interface ITestResult {
  userTestId: string
  title: string
  completed: boolean
  retries: number
  maxRetries: number
  date: Date
  score: number
  duration: number
  approved: boolean
  hit: number
  miss: number
  type: number
  status: string
  courseCompleted: boolean
}

export interface ISubmitAnswer {
  userTestId: string 
  questionId: string 
  answersId?: string[]
  answerDiscourse?: string
  scale?: number
  link?: string
}

export const assessmentService = {
  ...baseApi,
  resource: 'avaliacao',
  
  /**
   * Retorna as informações básicas de uma avaliação a ser realizada
   *
   * @param opts - Objeto com os parâmetros da requisição
   * @param opts.assessmentId - Id da avaliação
   * @param opts.courseId - Id do curso
   * @param opts.trailId - Id da trilha
   * @returns 
   */
  info<T>(opts: {
    assessmentId: string,
    courseId?: string,
    trailId?: string
  }) {
    return assessmentService.get<T>(`basic`, {
      avaliacaoId: opts.assessmentId,
      cursoId: opts?.courseId || null,
      trilhaId: opts?.trailId || null
    })
  },

  /**
   * Inicia uma avaliação
   *
   * @param opts - Objeto com os parâmetros da requisição
   * @param opts.assessmentId - Id da avaliação
   * @param opts.courseId - Id do curso
   * @param opts.trailId - Id da trilha
   * @returns 
   */
  start<T>(opts: {
    assessmentId: string,
    courseId?: string,
    trailId?: string
  }) {
    return assessmentService.post<T>(`student/start`, {
      avaliacaoId: opts.assessmentId,
      cursoId: opts.courseId || null,
      trilhaId: opts.trailId || null
    })
  },

  skipEvalution<T>(opt: {
    assessmentId: string, 
    courseId?: string,
    trailId?: string
  }) {
    return assessmentService.post<T>(`student/finish/dontAnswer`, {
      avaliacaoId: opt.assessmentId,
      cursoId: opt?.courseId || null,
      trilhaId: opt?.trailId || null
    })
  },

  uploadFile<T>(opt: {
    answerId: string
    type: 'document' | 'audio' | 'video' | 'image'
    file: File
  }) {
    const tps = {
      document: 'Documentos',
      audio: 'Audio',
      video: 'Video',
      image: 'Imagens'
    }

    const formData = new FormData()
    formData.append('file', opt.file)
    formData.append('name', opt.file.name)

    return assessmentService.post<T>(`student/${opt.answerId}/answer/${tps[opt.type]}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  setAssessmentExaminators(assessmentId: string, examinatorIds: string[]) {
    return assessmentService.put(`${assessmentId}/avaliadores`, examinatorIds)
  },

  setAssessmentExaminatorsInBulk(assessmentIds: string[], examinatorIds: string[]) {
    return assessmentService.put(`avaliadores`, 
      {
        usuariosIds: examinatorIds, 
        avaliacoesIds: assessmentIds
      })
  },

  getAssessmentsStudent() {
    return assessmentService.get<AssessmentsStudentType[]>(`student`)
  },

  getAssessmentsEvaluator() {
    return assessmentService.get<AssessmentsEvaluatorType[]>(`evaluator`)
  },

  getAssessmentsEvaluatorItems(assessmentId: string, contentId: string) {
    return assessmentService.get<AssessmentsEvaluatorItemsType[]>(`evaluator/${assessmentId}/${contentId}/items`)
  },

  getAssessmentsEvaluatorByStudent() {
    return assessmentService.get<AssessmentsEvaluatorType[]>(`evaluator-by-student`)
  },

  getAssessmentsEvaluatorItemsByStudent(userId: string) {
    return assessmentService.get<AssessmentsEvaluatorItemsType[]>(`evaluator/${userId}/items`)
  },

  async studentAssessmentForCorrection(userAssessmentId : string) {
    return await assessmentService.get<AssessmentCorrectionType>(`evaluator/${userAssessmentId}`)
  },

  async sendCorrectionAnswer(opts: { userAssessmentResponseId?: string, score?: number, observations?: string }) {
    try {
      return await assessmentService.post(`evaluator/correction-answer`, {
        userAssessmentResponseId: opts.userAssessmentResponseId,
        score: opts.score || 0,
        observations: opts.observations || ''
      })
      
    } catch (error) {
      console.log('Entrou no erro:', error);
      return { success: false, data: null };
    }
  },

  async alterCorrectionAnswer(opts: { evaluatorCorrectionId?: string, score?: number, observations?: string }) {
    try {
      return await assessmentService.put(`evaluator/${opts.evaluatorCorrectionId}/correction-answer`, {
        score: opts.score || 0,
        observations: opts.observations || ''
      })
      
    } catch (error) {
      console.log('entrou aqui no error', error)
      return { success: false, data: null }
    }
  },

  async finishCorrection(userAssessmentId: string, finalConsiderations?: string) {
    try {
      return await assessmentService.post(`evaluator/${userAssessmentId}/finish-correction`, {
        finalConsiderations: finalConsiderations || ''
      })
      
    } catch (error) {
      console.log('entrou aqui no error', error)
      return { success: false, data: null }
    }
  },

  uploadFileCorrection<T>(opt: {
    evaluatorCorrectionId : string
    type: 'document' | 'audio' | 'video' | 'image'
    file: File
  }) {
    const tps = {
      document: 'Documentos',
      audio: 'Audio',
      video: 'Video',
      image: 'Imagens'
    }

    const formData = new FormData()
    formData.append('file', opt.file)
    formData.append('name', opt.file.name)

    return assessmentService.post<T>(`evaluator/${opt.evaluatorCorrectionId}/correction-answer/${tps[opt.type]}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  }
}

class AvaliacaoService extends BaseService {
  constructor() {
    super({ resource: 'avaliacao' })
  }

  async getBonds(avaliacaoId: string) {
    return this.plantarApi.get(`${this.resource}/${avaliacaoId}/associations`)
  }

  async getInfo(opt: { courseId: string, trailId: string; testId: string }) {
    try {
      const { success, data } = await assessmentService.info<{
        titulo: string
        observacao: string
        totalQuestoes: number
        duracaoDaAvaliacao: string
        politicaRetentativa: string
        maximoTentativas: number
        tentativasRealizadas: number
        ultimaTentativa: string
        pontuacaoMinima: number
        tipoDeAvaliacao: string
        exibirCorretaseErradas: boolean
        exibirTimer: boolean,
        continuar: boolean,
        totalQuestoesParaResponder: number,
        totalQuestoesRespondidas: number
      }>({
        assessmentId: opt.testId,
        courseId: opt.courseId,
        trailId: opt.trailId
      })

      if (success) {
        return {
          success,
          data: {
            id: opt.testId,
            title: data.titulo,
            observation: data.observacao,
            maxQuestions: data.totalQuestoes,
            duration:
              data.duracaoDaAvaliacao
                ?.split(':')
                .reverse()
                .reduce((prev, curr, i) => prev + parseInt(curr) * Math.pow(60, i), 0) || 0, // Converte de XX:XX:XX para segundos
            canRetry: data.politicaRetentativa,
            retries: data.tentativasRealizadas,
            maxRetries: data.maximoTentativas,
            latestRetry: null,
            score: data.pontuacaoMinima,
            type: ['Diagnostica', 'Reacao', 'Aprendizagem', 'Comportamento'].findIndex((val) => val === data.tipoDeAvaliacao),
            displayAnswers: data.exibirCorretaseErradas,
            showTimer: data.exibirTimer,
            continue: data.continuar,
            totalQuestionsAnswered: data.totalQuestoesRespondidas,
            totalQuestionsToAnswer: data.totalQuestoesParaResponder
          }
        }
      }
    } catch (error) {
      //
    }

    return {
      success: false
    }
  }

  async start(opt: { courseId: string; trailId: string; testId: string }) {
    try {
      const { success, data } = await assessmentService.start<{ id: string, continuar: boolean,  dataInicio: string, questoesRespondidas: any[] }>({
        assessmentId: opt.testId,
        courseId: opt.courseId,
        trailId: opt.trailId
      })

      return {
        success,
        data: {
          id: data.id,
          continue: data.continuar,
          startTime: data.dataInicio,
          questionsAnswered: data.continuar ? data.questoesRespondidas : []
        }
      }
    } catch (error) {
      //
    }

    return { success: false }
  }

  async nextQuestion(userTestId?: string) {
    return this.plantarApi.get(`${this.resource}/student/${userTestId}/nextQuestion`)
  }

  async answer(answer: ISubmitAnswer) {
    try {
      const { success, data } = await this.plantarApi.post(`${this.resource}/student/answer`, {
        usuarioAvaliacaoId: answer.userTestId,
        questaoId: answer.questionId,
        alternativasIds: answer.answersId,
        texto: answer.answerDiscourse,
        escala: answer.scale,
        link: answer.link ?? null
      })

      return {
        success,
        data: {
          id: data.usuarioAvaliacaoRespostaId,
          correct: data.acertou,
          partiallyCorrect: data.acertouParcial,
          correctAnswers: data.alternativasCorretasIds,
          userAnswers: data.alternativasRespondidasIds,
          scale: data.escala,
          answerDiscourse: data.texto,
          link: data.link,
          hit: data.feedbackAcerto,
          miss: data.feedbackErro
        }
      }
    } catch (error) {
      return { success: false }
    }
  }

  async results(userTestId?: string) {
    try {
      const { success: s1, data: result } = await this.plantarApi.post<{
        id: string
        usuarioId: string
        avaliacaoId: string
        usuarioAvaliacaoId: string
        cursoId?: string
        formacaoId?: string
        dataAvaliacao: string
        concluida: boolean
        aprovado: boolean
        pontuacaoFinal: number
        tempoGastoEmMinutos: number
        acertos: number
        erros: number,
        status: string,
        aprovacao: {
          status: string | null
        }
      }>(`${this.resource}/student/${userTestId}/finish`)

      const { success: s2, data: info } = await this.getInfo({
        courseId: result.cursoId,
        trailId: result.formacaoId,
        testId: result.avaliacaoId
      })

      return {
        success: s1 && s2,
        data: {
          userTestId: result.usuarioAvaliacaoId,
          title: info.title,
          completed: result.concluida,
          retries: info.retries,
          maxRetries: info.maxRetries,
          date: new Date(result.dataAvaliacao),
          score: result.pontuacaoFinal,
          duration: result.tempoGastoEmMinutos,
          approved: result.aprovado,
          hit: result.acertos,
          miss: result.erros,
          type: info.type,
          status: result.status,
          courseCompleted: result?.aprovacao?.status === 'Aprovado'
        } as ITestResult
      }
    } catch (error) {
      return { success: false }
    }
  }

  async skip(opt: { assessmentId: string, courseId?: string, trailId?: string }) {
    try {
      const { success, data } = await assessmentService.skipEvalution<{
        usuarioAvaliacaoId: string
        aprovacao: {
          status: string | null
        }
      }>({
        assessmentId: opt.assessmentId,
        courseId: opt?.courseId || null,
        trailId: opt?.trailId || null
      })

      return {
        success,
        data: {
          userTestId: data.usuarioAvaliacaoId,
          courseCompleted: data?.aprovacao?.status === 'Aprovado'
        }
      }
    } catch (error) {
      //
    }

    return { success: false }
  }

  async giveUp(userTestId: string) {
    if (!userTestId) return Promise.resolve({ success: false })

    try {
      const { success } = await this.plantarApi.put(`${this.resource}/student/${userTestId}/finish/giveup`)
      return { success }
    } catch (error) {
      return { success: false }
    }
  }
}

const avaliacaoService = new AvaliacaoService()

export default avaliacaoService
